import { EvaluatorValue, Fact, ValidatorType } from '../base';
import { MessageCollection, TextMessage, Translation } from '../i18n';
import { ExecutionContext } from '../runtime';
import { ValidationContext } from './context';
import { ValidationError } from './errors';
import { Validator } from './validator';

export class NetworksNoOverlapValidator extends Validator {
  cidr?: string;
  compareWith?: string;
  // Shown in the messages; callers should set the question titles. The
  // defaults read naturally when they don't ("The value is not a valid ...").
  cidrName: TextMessage = [new Translation('en', 'The value'), new Translation('de', 'Der Wert')];
  compareWithName: TextMessage = [
    new Translation('en', 'the other network'),
    new Translation('de', 'dem anderen Netzwerk'),
  ];

  static readonly error_overlap_long = 'default/validators/networks/errors/overlap/long';
  static readonly error_invalid_cidr_long = 'default/validators/networks/errors/invalid_cidr/long';

  constructor(type?: ValidatorType) {
    super(type !== undefined ? type : ValidatorType.IpComparison);
  }

  public validate(ctx: ExecutionContext, fact: Fact<EvaluatorValue>): ValidationContext {
    const result = new ValidationContext(fact);
    // The names are TextMessages (usually Translation[]); interpolating them
    // directly printed "CIDR A,CIDR A". Pick the text for each language.
    const nameA = (lang: string): string => textFor(this.cidrName, lang);
    const nameB = (lang: string): string => textFor(this.compareWithName, lang);
    MessageCollection.Global.setTranslations(NetworksNoOverlapValidator.error_overlap_long, [
      new Translation('en', `${nameA('en')} overlaps the IP range of ${nameB('en')}`),
      new Translation(
        'de',
        `${nameA('de')} hat einen überlappenden IP-Bereich mit ${nameB('de')}`
      ),
    ]);

    MessageCollection.Global.setTranslations(NetworksNoOverlapValidator.error_invalid_cidr_long, [
      new Translation('en', `${nameA('en')} is not a valid IPv4 network CIDR`),
      new Translation('de', `${nameA('de')} ist keine gültige IPv4-Netzwerk-CIDR`),
    ]);

    // The network being checked is the question's own answer, unless a caller
    // pinned `cidr` explicitly. Before, only `cidr`/`compareWith` were read -
    // and nothing set them (the catalog wiring is disabled) - so every answer,
    // valid or not, was reported as an invalid CIDR.
    const own = (this.cidr ?? factText(fact)).trim();
    const other = (this.compareWith ?? '').trim();

    // Empty is not "invalid": whether an answer is required is decided
    // elsewhere. Nothing to compare against means only the format is checked.
    if (own === '') {
      return result;
    }
    if (!isValidCidr(own)) {
      result.diagnostics.push(
        new ValidationError(
          NetworksNoOverlapValidator.error_invalid_cidr_long,
          `cidr "${own}" is not a valid CIDR`,
          this
        )
      );
      return result;
    }

    if (other !== '' && isValidCidr(other)) {
      try {
        if (!networksDoNotOverlap(own, other)) {
          result.diagnostics.push(
            new ValidationError(
              NetworksNoOverlapValidator.error_overlap_long,
              'CIDRs overlap',
              this
            )
          );
        }
      } catch (e) {
        result.diagnostics.push(
          new ValidationError(
            NetworksNoOverlapValidator.error_overlap_long,
            'Error during overlap check',
            this,
            e
          )
        );
      }
    }

    return result;
  }

  public read(o: any): this {
    super.read(o);
    if (Object.prototype.hasOwnProperty.call(o, 'cidrA')) {
      this.cidr = o.cidrA;
    }
    if (Object.prototype.hasOwnProperty.call(o, 'cidrB')) {
      this.compareWith = o.cidrB;
    }
    return this;
  }

  public save(): any {
    const result = super.save();
    if (this.cidr) {
      result.cidrA = this.cidr;
    }
    if (this.compareWith) {
      result.cidrB = this.compareWith;
    }
    return result;
  }
}

const textFor = (msg: TextMessage, lang: string): string => {
  if (typeof msg === 'string') {
    return msg;
  }
  if (Array.isArray(msg)) {
    const hit = msg.find((t) => t.language === lang) ?? msg[0];
    return hit ? hit.message : '';
  }
  if (msg instanceof Translation) {
    return msg.message;
  }
  return String(msg);
};

const factText = (fact: Fact<EvaluatorValue> | undefined): string => {
  const raw = (fact as { raw?: unknown } | undefined)?.raw;
  return raw === undefined || raw === null ? '' : String(raw);
};

const cidrRegex =
  /^(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\/([0-9]|[1-2][0-9]|3[0-2])$/;

function isValidCidr(cidr: string): boolean {
  return cidrRegex.test(cidr);
}

const ipToInt = (ip: string): number => {
  return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0);
};

function networksDoNotOverlap(cidrA: string, cidrB: string): boolean {
  const parseCidr = (cidr: string) => {
    const [ip, prefixStr] = cidr.split('/');
    const prefix = parseInt(prefixStr, 10);
    const net = ipToInt(ip);
    const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
    const network = net & mask;
    const broadcast = network | (~mask >>> 0);
    return { network, broadcast };
  };

  if (!isValidCidr(cidrA) || !isValidCidr(cidrB)) {
    return !isValidCidr(cidrA) || !isValidCidr(cidrB);
  }

  const a = parseCidr(cidrA);
  const b = parseCidr(cidrB);

  return a.broadcast < b.network || b.broadcast < a.network;
}
